import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BliksLoader } from '@bliks-io/ui';
import { useInitializeUnresolvedDepartments } from '@/api/queries/useInitializeUnresolvedDepartments';
import { useOrgMembers } from '@/api/queries/useOrgMembers';
import { useDepartments } from '@/api/queries/useDepartments';
import { usePersonioSetupRows } from '@/api/queries/useDataSync';
import { WizardStatCard } from '../components/WizardStatCard';
import {
  DepartmentResolutionTable,
  type ManagerSuggestion
} from '../components/DepartmentResolutionTable';
import { WizardCallout } from '../components/WizardCallout';
import { WizardStepHeader } from '../components/WizardStepHeader';
import { normalizedKey } from '@/lib/draft-rows/normalizedKey';
import type { User } from '@bliks-io/contracts';

interface ManagerResolutionStepProps {
  managerAssignments: Map<string, string>;
  onAssignmentChange: (deptId: string, managerId: string | undefined) => void;
  isSubmitting?: boolean;
}

export function ManagerResolutionStep({
  managerAssignments,
  onAssignmentChange,
  isSubmitting = false
}: ManagerResolutionStepProps) {
  const { t } = useTranslation();
  const { data: orgMembers } = useOrgMembers({
    status: ['inactive', 'pending', 'active'],
    classification: 'internal'
  });
  const { data: resolvedDepartments = [] } = useDepartments();
  const { data: setupRows } = usePersonioSetupRows();

  const emailToUser = useMemo(() => {
    const map = new Map<string, User>();
    orgMembers?.forEach((user) => map.set(user.email.toLowerCase(), user));
    return map;
  }, [orgMembers]);

  const { data: unresolvedDepartments, isLoading } = useInitializeUnresolvedDepartments();

  // By name only: the source abbreviations are generated, so another department
  // can hold one. Only a suggested person who is a member can be taken over.
  const managerSuggestions = useMemo(() => {
    const suggestions = setupRows?.managerSuggestions ?? [];
    const byName = new Map(suggestions.map((s) => [normalizedKey(s.departmentName), s]));
    const byDepartmentId = new Map<string, ManagerSuggestion>();
    for (const dept of unresolvedDepartments ?? []) {
      const suggestion = byName.get(normalizedKey(dept.name));
      const user = suggestion && emailToUser.get(suggestion.userEmail.toLowerCase());
      if (suggestion && user) {
        byDepartmentId.set(dept._id, { displayName: suggestion.displayName, user });
      }
    }
    return byDepartmentId;
  }, [setupRows, unresolvedDepartments, emailToUser]);

  const openCount = unresolvedDepartments?.length ?? 0;
  const resolvedCount = resolvedDepartments.filter((dept) => dept.manager).length;
  const hasUnresolvedDepartments = openCount > 0;

  if (isLoading) {
    return (
      <div className="py-12">
        <BliksLoader label={t('bulkOnboarding.resolution.loading')} />
      </div>
    );
  }

  return (
    <div className="space-y-6 px-10 pb-6 pt-9">
      <WizardStepHeader
        title={t('initialSetup.steps.resolveManagers')}
        description={
          hasUnresolvedDepartments
            ? t('bulkOnboarding.resolution.description')
            : t('bulkOnboarding.resolution.success.description')
        }
      />

      <div className="flex gap-4">
        <WizardStatCard
          className="flex-1 px-5 py-4.5"
          tone="warning"
          icon={<span className="h-2 w-2 rounded-full bg-warning-solid" />}
          label={t('initialSetup.resolve.counters.open')}
          value={openCount}
          valueClassName="text-[32px]"
        />
        <WizardStatCard
          className="flex-1 px-5 py-4.5"
          icon={<span className="h-2 w-2 rounded-full bg-bliks-green-600" />}
          label={t('initialSetup.resolve.counters.resolved')}
          value={resolvedCount}
          valueClassName="text-[32px]"
        />
      </div>

      {!hasUnresolvedDepartments && (
        <WizardCallout variant="success" title={t('bulkOnboarding.resolution.success.title')}>
          {t('bulkOnboarding.resolution.success.description')}
        </WizardCallout>
      )}

      {(hasUnresolvedDepartments || resolvedCount > 0) && (
        <DepartmentResolutionTable
          departments={unresolvedDepartments ?? []}
          resolvedDepartments={resolvedDepartments}
          managerAssignments={managerAssignments}
          emailToUser={emailToUser}
          managerSuggestions={managerSuggestions}
          onAssignmentChange={onAssignmentChange}
          isSubmitting={isSubmitting}
        />
      )}

      {hasUnresolvedDepartments && (
        <WizardCallout variant="info">{t('bulkOnboarding.resolution.instructions')}</WizardCallout>
      )}
    </div>
  );
}
